'use client';
import { logger } from '@magicschool/logger';

export async function loadMammoth() {
  if (typeof window !== 'undefined' && window.mammoth) return window.mammoth;

  try {
    await _injectScript('https://cdnjs.cloudflare.com/ajax/libs/mammoth/1.7.2/mammoth.browser.min.js');
    return window.mammoth;
  } catch (error) {
    logger.error('Failed to load mammoth.js library', { error });
    throw new Error('Failed to load Word document processing library');
  }
}

export async function getGoogle() {
  if (typeof window !== 'undefined' && window.google) return window.google;

  try {
    await Promise.all([_injectScript('https://apis.google.com/js/api.js'), _injectScript('https://accounts.google.com/gsi/client')]);

    return window.google;
  } catch (error) {
    logger.error('Failed to load Google API script:', error);
    throw new Error('Failed to load Google API');
  }
}

/**
 * Load a script dynamically and on-demand!
 *
 * - uses a promise to ensure the script is loaded before resolving.
 * - supports async and defer attributes.
 * - supports module type for ES modules.
 * - defaults to text/javascript for regular scripts.
 *
 * When importing more than one script, this method performs better than `import`.
 *
 * If the script is already present, it resolves immediately with the existing script's promise.
 * This allows multiple parts of the application to wait for the same script without loading it multiple times.
 *
 * this helps us avoid race conditions where the script is already being loaded. This means many places can be waiting for a script to load.
 * @param src
 * @param param1
 * @returns
 */
export const _injectScript = (
  src: string,
  { async, defer, type = 'text/javascript' }: { async?: boolean; defer?: boolean; type?: 'module' | 'text/javascript' } = {},
): Promise<unknown> => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return Promise.resolve();

  const { promise, resolve, reject } = unpackPromise();

  // check if the script is already loaded
  const existingScript: HTMLScriptElement | null = document.querySelector(`script[src="${src}"]`);
  if (existingScript != null) {
    // Script already loaded/loading: ${src}
    // @ts-ignore
    resolve(existingScript._promise);
    return promise;
  }

  const script = document.createElement('script');
  script.src = src;
  script.async = async || false;
  script.defer = defer || false;
  script.type = type;
  document.head.appendChild(script);

  // @ts-expect-error
  // Store the promise on the script element for potential re-reference, avoids race conditions (above)
  script._promise = promise;
  script.onload = () => {
    resolve();
    // @ts-expect-error - Clear the promise reference after loading
    script._promise = null;
  };

  script.onerror = (event: Event | string) => {
    logger.error('Failed to load script:', event);
    reject(new Error(`Failed to load script: ${event.toString()}`));
  };

  script.onabort = () => {
    logger.warn('Script loading was aborted:', src);
  };

  return promise;
};

function unpackPromise() {
  let _resolve: (value?: unknown) => void;
  let _reject: (reason?: unknown) => void;

  const _promise = new Promise<unknown>((resolve, reject) => {
    _resolve = resolve;
    _reject = reject;
  });
  // @ts-ignore - use before?
  return { promise: _promise, resolve: _resolve, reject: _reject };
}
